// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { HideFooter, HideHeader } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem, LazyImage, Link } from "@insite/mobius/index";
import { LazyImagePresentationProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CalenderExport from "@stanion/App/Components/Common/CalenderExport";
import XngZone from "@stanion/App/Components/Common/XngZone";
import { formatDateTime, xScreen } from "@stanion/XDL/Utilities";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        language: state.context.session.language,
    };
};

type Props = PageProps & ReturnType<typeof mapStateToProps>;

export interface EventStyles {
    container?: GridContainerProps;
    fullWidthGridItem?: GridItemProps;
    halfWidthGridItem?: GridItemProps;
    topSectionGridItem?: GridItemProps;
    middleSectionGridItem?: GridItemProps;
    bottomSectionGridItem?: GridItemProps;
    leftSectionGridItem?: GridItemProps;
    imageGridItem?: GridItemProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    titleLocationText?: TypographyProps;
    leftText?: TypographyProps;
    richContentText?: TypographyProps;
    registrationLink?: LinkPresentationProps;
    bottomSectionForm?: InjectableCss;
    bottomSectionContainer?: InjectableCss;
    imageStyle?: InjectableCss;
    calenderExport?: InjectableCss;
    courseDescription?: InjectableCss;
}
export const eventStyles: EventStyles = {
    container: {
        gap: 30,
    },
    fullWidthGridItem: {
        width: 12,
    },
    halfWidthGridItem: {
        width: 6,
    },
    imageGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            max-height: 260px;
            justify-content: flex-end;
            ${xScreen("lte", "sm")} {
                justify-content: flex-start;
            }
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    leftSectionGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            row-gap: 4px;
        `,
    },
    titleText: {
        as: "h1",
        variant: "h1",
        css: css`
            margin-bottom: 0px;
        `,
    },
    titleLocationText: {
        as: "h3",
        weight: "normal",
        variant: "h3",
    },
    richContentText: {
        css: css`
            margin-top: 25px;
        `,
    },
    bottomSectionForm: {
        css: css`
            display: flex;
            column-gap: 20px;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                row-gap: 25px;
                > form {
                    margin: 0;
                }
            }
            > div:empty {
                display: none;
            }
        `,
    },
    bottomSectionContainer: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    imageStyle: {
        css: css`
            img {
                object-fit: contain;
            }
        `,
    },
    calenderExport: {
        css: css`
            height: 100%;
            margin-left: auto;
            display: flex;
            align-items: center;
            ${xScreen("lte", "sm")} {
                margin-left: 0;
            }
        `,
    },
    middleSectionGridItem: {
        width: 12,
        css: css`
            column-gap: 30px;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                row-gap: 15px;
            }
        `,
    },
};
const styles = eventStyles;

const EventPage: React.FunctionComponent<Props> = ({ id, fields, language }) => {
    const history = useHistory();

    const startDate = getLocalizedDateTime({
        dateTime: new Date(fields.eventStartDate),
        language,
        options: {
            month: "short",
            day: "numeric",
        },
    });
    const endDate = getLocalizedDateTime({
        dateTime: new Date(fields.eventEndDate),
        language,
        options: {
            month: "short",
            day: "numeric",
        },
    });

    const eventStartTimeFormat = formatDateTime(new Date(fields.eventStartDate));
    const eventEndTimeFormat = formatDateTime(new Date(fields.eventEndDate));

    return (
        <Page>
            <GridContainer {...styles.container}>
                <GridItem {...styles.fullWidthGridItem}>
                    <GridContainer gap={20}>
                        <GridItem {...styles.titleGridItem}>
                            <Typography {...styles.titleText}>{fields.title}</Typography>
                            <Typography {...styles.titleLocationText}>{fields.location}</Typography>
                        </GridItem>
                        <GridItem {...styles.leftSectionGridItem}>
                            <Typography>{fields.address}</Typography>
                            <Typography>
                                {translate(`${fields.streetAddress} , ${fields.state} ${fields.postalCode}`)}
                            </Typography>
                            <Typography>{translate(`Date: ${startDate} - ${endDate} `)}</Typography>
                            <Typography>
                                {translate(`Time: ${eventStartTimeFormat} - ${eventEndTimeFormat} `)}
                            </Typography>
                            {fields.courseCode && (
                                <Typography>{translate(`Course Code: ${fields.courseCode} `)}</Typography>
                            )}
                            {fields.price && <Typography>{translate(`Price: $ ${fields.price} `)}</Typography>}
                            {fields.richContent && (
                                <Typography {...styles.richContentText}>
                                    {parse(fields.richContent, parserOptions)}
                                </Typography>
                            )}
                        </GridItem>
                        <GridItem {...styles.imageGridItem}>
                            <LazyImage src={fields.image} altText={fields.title} {...styles.imageStyle} />
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.middleSectionGridItem}>
                    {fields.courseDescription && (
                        <StyledWrapper {...styles.courseDescription}>
                            {parse(fields.courseDescription, parserOptions)}
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.calenderExport}>
                        <CalenderExport
                            startDate={fields.eventStartDate}
                            endDate={fields.eventEndDate}
                            title={fields.title}
                            location={fields.location}
                            isOneDayEvent={startDate === endDate}
                        />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    {!fields.registration ? (
                        <Link href={fields.registrationUrl.value} {...styles.registrationLink}>
                            {siteMessage("Custom_EventPage_RegistrationLink")}
                        </Link>
                    ) : (
                        <StyledWrapper {...styles.bottomSectionContainer}>
                            <Typography variant="h2">{translate("REQUEST YOUR SPOT")}</Typography>
                            <StyledWrapper {...styles.bottomSectionForm}>
                                <XngZone contentId={id} zoneName="content" />
                                {createWidgetElement("EventRegistration/EventRegistration", {
                                    fields: { url: fields.urlSegment },
                                })}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <XngZone contentId={id} zoneName="content1" />
                </GridItem>
            </GridContainer>
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, null)(EventPage),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "Content",
        fieldDefinitions: [
            HideHeader,
            HideFooter,
            {
                name: "title",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "location",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "type",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "courseCode",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: false,
            },
            {
                name: "courseDescription",
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                extendedConfig: { height: 100 },
                isRequired: true,
            },
            {
                name: "streetAddress",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "city",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "state",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "postalCode",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "eventStartDate",
                defaultValue: null,
                editorTemplate: "DateTimeField",
                fieldType: "General",
                isRequired: true,
                validate: value => (value && value >= new Date() ? null : "Date should be Greater from Now!"),
            },
            {
                name: "eventEndDate",
                defaultValue: null,
                editorTemplate: "DateTimeField",
                fieldType: "General",
                isRequired: true,
                validate: (value, item) =>
                    value && item.fields.eventStartDate < value
                        ? null
                        : "Please enter End Date Greater than Start Date",
            },
            {
                name: "price",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                validate: value => {
                    if (!value) {
                        return null;
                    }
                    return new RegExp(/^\d*\.?\d+$/).test(value) ? null : "Price should be a number";
                },
                isRequired: false,
            },
            {
                name: "richContent",
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                isRequired: true,
                extendedConfig: { height: 100 },
            },
            {
                name: "image",
                defaultValue: "",
                editorTemplate: "ImagePickerField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "registrationUrl",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                fieldType: "General",
                isRequired: true,
                isVisible: item => item.fields.registration === false,
            },
            {
                name: "registration",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default pageModule;

export const EventPageContext = "EventPage";
